import Image from 'next/image';
import NextLink from 'next/link';

import { Box, Button, Flex, Heading, LinkBox, LinkOverlay, Text } from '@chakra-ui/react';

import { CircleArrowRightIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import {
  DIWALI_2026_ASSETS,
  DIWALI_2026_CATALOG_BG_GRADIENT,
  DIWALI_2026_COLORS,
  DIWALI_2026_COPY,
  DIWALI_2026_CTA_LABEL,
} from '~/features/promotions/constants/diwali-2026';

export function Diwali2026CatalogBanner({
  checkoutHref,
  imagePriority,
  onClick,
}: {
  checkoutHref: string;
  imagePriority?: boolean;
  onClick: () => void;
}) {
  return (
    <LinkBox
      backgroundColor={DIWALI_2026_COLORS.background}
      border='1px solid'
      borderColor={DIWALI_2026_COLORS.catalogBorder}
      borderRadius='8px'
      height='100%'
      isolation='isolate'
      minHeight={{ base: '137px', md: '120px' }}
      overflow='hidden'
      position='relative'
      width='100%'
    >
      <CatalogBackground imagePriority={imagePriority} />
      <Flex
        alignItems={{ base: 'stretch', md: 'center' }}
        height='100%'
        minHeight={{ base: '135px', md: '118px' }}
        padding={{ base: '16px', md: '32px' }}
        position='relative'
        zIndex={1}
      >
        <Flex
          alignItems={{ base: 'flex-start', md: 'flex-end' }}
          flex={1}
          flexDirection={{ base: 'column', md: 'row' }}
          gap={{ base: 3, md: 4 }}
          justifyContent='space-between'
          minWidth={0}
        >
          <Flex flexDirection='column' gap={2} minWidth={0}>
            <Heading
              as='h3'
              color='white'
              fontSize={{ base: '1.375rem', md: '1.5rem' }}
              fontWeight='600'
              letterSpacing={{ base: 'normal', md: '-0.01em' }}
              lineHeight={{ base: 1.2, md: 1.15 }}
            >
              <Box as='span' display={{ base: 'block', md: 'inline' }}>
                {DIWALI_2026_COPY.catalogHeadlineLine1}
              </Box>{' '}
              {DIWALI_2026_COPY.catalogHeadlineLine2}
            </Heading>
            <Text
              color={DIWALI_2026_COLORS.catalogSubtitle}
              display={{ base: 'none', md: 'block' }}
              fontFamily='heading'
              fontSize='18px'
              fontWeight='600'
              letterSpacing='-0.01em'
              lineHeight={1.1}
            >
              {DIWALI_2026_COPY.catalogSubtitle}
            </Text>
          </Flex>
          <Button
            aria-hidden={true}
            as='span'
            backgroundColor={DIWALI_2026_COLORS.catalogCta}
            borderRadius='4px'
            color='white'
            display='inline-flex'
            flexShrink={0}
            fontSize='12px'
            fontWeight='400'
            gap='10px'
            height='auto'
            iconSpacing={0}
            leftIcon={<CircleArrowRightIcon boxSize='14px' color='white' />}
            lineHeight={1.4}
            paddingBottom='10px'
            paddingLeft='12px'
            paddingRight='16px'
            paddingTop='10px'
            pointerEvents='none'
            tabIndex={-1}
            variant='unstyled'
          >
            {DIWALI_2026_CTA_LABEL}
          </Button>
        </Flex>
      </Flex>
      <LinkOverlay
        aria-label={`${DIWALI_2026_COPY.catalogHeadlineLine1} ${DIWALI_2026_COPY.catalogHeadlineLine2} ${DIWALI_2026_CTA_LABEL}`}
        as={NextLink}
        href={checkoutHref}
        inset={0}
        position='absolute'
        zIndex={2}
        onClick={onClick}
      />
    </LinkBox>
  );
}

function CatalogBackground({ imagePriority }: { imagePriority?: boolean }) {
  return (
    <Box
      backgroundImage={DIWALI_2026_CATALOG_BG_GRADIENT}
      bottom={0}
      left={0}
      pointerEvents='none'
      position='absolute'
      right={0}
      top={0}
    >
      {/* Flower art was exported with the gradient removed; screen restores the Figma composite. */}
      <Box display={{ base: 'block', md: 'none' }} height='100%' mixBlendMode='screen' position='relative' width='100%'>
        <Image
          alt=''
          loading='eager'
          priority={imagePriority}
          sizes='100vw'
          src={DIWALI_2026_ASSETS.catalogBgMobile}
          style={{ objectFit: 'cover', objectPosition: 'right top' }}
          fill
        />
      </Box>
      <Box display={{ base: 'none', md: 'block' }} height='100%' mixBlendMode='screen' position='relative' width='100%'>
        <Image
          alt=''
          loading='eager'
          priority={imagePriority}
          sizes='656px'
          src={DIWALI_2026_ASSETS.catalogBg}
          style={{ objectFit: 'cover', objectPosition: 'right center' }}
          fill
        />
      </Box>
    </Box>
  );
}
